import { handleTransition } from 'Util/transitions.js';
import { validateField } from 'app_vodafone_ziggo/util/validation';
import { clearFormFields, prefillFormFields, makeContactFormFieldsRequired } from 'app_vodafone_ziggo/helpers/formHelpers';

export default class VZDeliveryPlanner extends HTMLElement {
    constructor() {
        super();

        this.deliveryPlanner = this.closest('.js-delivery-planner');

        // MAIN CONTACT
        this.mainContactData = JSON.parse(this.deliveryPlanner.getAttribute('data-main-contact')) || null;

        // MAIN TECHNICAL CONTACT
        this.installationSection = document.querySelector('.js-installation');
        this.MTCEnabled = this.installationSection?.getAttribute('data-main-technical-contact-enabled') === 'true';
        this.MTCSection = this.MTCEnabled ? document.querySelector('.js-main-technical-contact') : null;

        // Initialize MTC properties only when feature is enabled
        if (this.MTCEnabled && this.MTCSection) {
            this.MTCCheckbox = this.MTCSection.querySelector('.js-main-technical-contact-checkbox');
            this.MTCForm = this.MTCSection.querySelector('.js-main-technical-contact-form');
            this.MTCFullName = this.MTCSection.querySelector('.js-main-technical-contact-fullname');
            this.MTCFormElements = VZDeliveryPlanner.getContactFormElements(this.MTCSection);
            this.MTCFormValues = VZDeliveryPlanner.getContactFormValues(this.MTCFormElements);
        }

        // INSTALLATION SITES
        this.installationSites = document.querySelectorAll('.js-installation-site');
    }

    /**
     * Retrieves contact form elements from a specified section of the form.
     *
     * @param {HTMLElement} formSection - The section of the form to retrieve elements from.
     * @returns {Object} An object containing references to the contact form elements.
     */
    static getContactFormElements(formSection) {
        return {
            initials: formSection.querySelector('.js-contact-person-initials'),
            middleName: formSection.querySelector('.js-contact-person-middlename'),
            lastName: formSection.querySelector('.js-contact-person-lastname'),
            email: formSection.querySelector('.js-contact-person-email'),
            phonePrefix: formSection.querySelector('.js-contact-person-phoneprefix'),
            phone: formSection.querySelector('.js-contact-person-phone')
        };
    }

    /**
     * Extracts and returns the values from the contact form elements.
     *
     * @param {Object} formElements - An object containing references to the contact form elements.
     * @returns {Object} An object containing the values of the contact form elements.
     */
    static getContactFormValues(formElements) {
        let formValues;

        if (formElements.initials) {
            // Map values
            formValues = {
                initials: formElements.initials.value || '',
                middleName: formElements.middleName.value || '',
                lastName: formElements.lastName.value || '',
                email: formElements.email.value || '',
                phonePrefix: formElements.phonePrefix.value || '',
                phone: formElements.phone.value || ''
            };
        } else {
            // Map values from 'billingData.installationData' object
            formValues = {
                initials: formElements.firstName || '',
                middleName: formElements.middleName || '',
                lastName: formElements.lastName || '',
                email: formElements.email || '',
                phonePrefix: formElements.phonePrefix || '',
                phone: formElements.phone || ''
            };
        }

        return formValues;
    }

    /**
     * Validates the prefix field for the given form data if the field exists.
     *
     * @param {Object} formElements - An object containing references to the contact form elements.
     */
    static validatePrefixField(formElements) {
        if (formElements && formElements.phonePrefix) validateField(formElements.phonePrefix);
    }

    /**
     * Keeps aria-expanded state in sync with checkbox state.
     *
     * @param {HTMLInputElement} checkbox - Checkbox controlling a collapsible form section.
     */
    static syncExpandedState(checkbox) {
        if (checkbox) {
            checkbox.setAttribute('aria-expanded', checkbox.checked ? 'true' : 'false');
        }
    }

    connectedCallback() {
        // Event listener for the main technical contact checkbox
        if (this.MTCEnabled && this.MTCCheckbox) {
            this.MTCCheckbox.addEventListener('change', this.toggleMainTechnicalContact.bind(this));

            // Listen for changes in the main technical contact form to update the contact display name accordingly and automatically fill in the installation site forms with the same information if applicable.
            Object.values(this.MTCFormElements).forEach((input) => {
                input.addEventListener('input', () => {
                    const fullName = `${this.MTCFormElements.initials.value} ${this.MTCFormElements.middleName.value} ${this.MTCFormElements.lastName.value}`;

                    this.MTCFullName.textContent = fullName.trim();

                    this.MTCFormValues = VZDeliveryPlanner.getContactFormValues(this.MTCFormElements);

                    this.installationSites.forEach((site) => {
                        const ISCSection = site.querySelector('.js-installation-contact');
                        const ISCCheckbox = site.querySelector('.js-contact-person-checkbox');
                        const ISCFormElements = VZDeliveryPlanner.getContactFormElements(site);

                        if (!ISCCheckbox.checked) {
                            // Update display name
                            ISCSection.querySelector('.js-installation-contact-fullname').textContent = fullName.trim();
                            // Update form field values with MTC data
                            prefillFormFields(ISCFormElements, this.MTCFormValues);
                        }
                    });
                });
            });
        }

        // Iterates through each installation site to set up event listeners for the contact person checkbox and input fields.
        // This enables toggling the contact person for an installation site and updating the full name display based on input.
        this.installationSites.forEach((site) => {
            // INSTALLATION SITE CONTACT
            const ISCSection = site.querySelector('.js-installation-contact');
            const ISCCheckbox = site.querySelector('.js-contact-person-checkbox');
            const ISCFormElements = VZDeliveryPlanner.getContactFormElements(ISCSection);

            // Event listener for the installation site contact checkbox
            ISCCheckbox.addEventListener('change', this.toggleInstallationSiteContact.bind(this, site));

            // Listen for changes in the installation site contact forms to update the contact display name accordingly.
            Object.values(ISCFormElements).forEach((input) => {
                input.addEventListener('input', () => {
                    ISCSection.querySelector('.js-installation-contact-fullname').textContent =
                        `${ISCFormElements.initials.value} ${ISCFormElements.middleName.value} ${ISCFormElements.lastName.value}`.trim();
                });
            });
        });

        // Set initial state for main technical contact form
        if (this.MTCEnabled) {
            this.setInitialStateMainTechnicalContact();
        }

        // Set initial state for wish date form
        this.setInitialStateInstallationWishDate();

        // Set initial state for installation site contact form
        this.setInitialStateInstallationSiteContact();
    }

    /**
     * Sets the initial state of the main technical contact form elements and prefill them with data if applicable.
     */
    setInitialStateMainTechnicalContact() {
        if (!this.MTCEnabled || !this.MTCFormElements) return;
        VZDeliveryPlanner.syncExpandedState(this.MTCCheckbox);

        // Validate the phone prefix field of the main technical contact form
        VZDeliveryPlanner.validatePrefixField(this.MTCFormElements);

        if (this.mainContactData) {
            // Make main technical contact form fields required if checkbox is checked
            makeContactFormFieldsRequired(this.MTCFormElements, this.MTCCheckbox.checked);

            // Prefill the main technical contact form fields
            if (this.MTCCheckbox.checked) {
                prefillFormFields(this.MTCFormElements, this.MTCFormValues);
            } else {
                prefillFormFields(this.MTCFormElements, this.mainContactData);
            }
        }
    }

    /**
     * Sets the initial state of the installation wish date form elements and prefill them with data if applicable.
     */
    setInitialStateInstallationWishDate() {
        // Validate the phone prefix field of the installation site contact forms
        this.installationSites.forEach((site) => {
            const ISData = JSON.parse(site.getAttribute('data-installation-site')) || null;

            // If there is no data to pre-fill, continue
            if (!ISData) return;

            const IWDCheckbox = site.querySelector('.js-wishdate-checkbox');
            const IWDDatepicker = site.querySelector('.js-wishdate-datepicker');

            // Trigger datepicker update
            if (IWDDatepicker && IWDCheckbox && ISData.wishDate) {
                const event = {
                    bubbles: true,
                    cancelable: true,
                    detail: { date: ISData.wishDate, asap: ISData.asapDate || null }
                };

                IWDDatepicker.dispatchEvent(new CustomEvent('setDatePickerDate', event));
            }
        });
    }

    /**
     * Sets the initial state of the installation site form elements and prefill them with data if applicable.
     */
    setInitialStateInstallationSiteContact() {
        // Validate the phone prefix field of the installation site contact forms
        this.installationSites.forEach((site) => {
            const ISData = JSON.parse(site.getAttribute('data-installation-site')) || null;
            const ISCCheckbox = site.querySelector('.js-contact-person-checkbox');
            VZDeliveryPlanner.syncExpandedState(ISCCheckbox);
            const ISCFormElements = VZDeliveryPlanner.getContactFormElements(site);
            const ISCFormValues = ISData ? VZDeliveryPlanner.getContactFormValues(ISData) : VZDeliveryPlanner.getContactFormValues(ISCFormElements);

            // Validate the phone prefix field of the installation site contact form
            if (ISCFormElements) VZDeliveryPlanner.validatePrefixField(ISCFormElements);

            if (this.mainContactData) {
                // Make installation site form fields required if checkbox is checked
                makeContactFormFieldsRequired(ISCFormElements, ISCCheckbox.checked);

                // Prefill the installation site contact form fields
                if (ISCCheckbox.checked) {
                    prefillFormFields(ISCFormElements, ISCFormValues);
                } else if (this.MTCEnabled && this.MTCCheckbox && this.MTCCheckbox.checked) {
                    // If MTC is enabled and checked, use MTC data
                    prefillFormFields(ISCFormElements, this.MTCFormValues);
                } else {
                    // Fallback to main contact data (when MTC is disabled or not checked)
                    prefillFormFields(ISCFormElements, this.mainContactData);
                }
            }
        });
    }

    /**
     * Toggles the display and state of the Main Technical Contact form based, and will prefill or clear the form based on the checkbox status.
     *
     * @param {Event} [event] - The change event from the checkbox (optional).
     */
    toggleMainTechnicalContact(event) {
        if (!this.MTCEnabled) return;

        // Prevent the immediate change event from triggering form validation
        event?.stopPropagation();

        handleTransition(() => {
            VZDeliveryPlanner.syncExpandedState(this.MTCCheckbox);
            // Toggle the form display based on the checkbox status.
            this.MTCForm.classList.toggle('d-none', !this.MTCCheckbox.checked);

            if (!this.MTCCheckbox.checked) {
                // When the checkbox is unchecked, prefill the form with main contact data and set fields as not required.
                prefillFormFields(this.MTCFormElements, this.mainContactData);
                makeContactFormFieldsRequired(this.MTCFormElements, false);
            } else {
                // When the checkbox is checked, clear the form fields and set them as required.
                clearFormFields(this.MTCFormElements);
                makeContactFormFieldsRequired(this.MTCFormElements, true);

                // Validate the phone prefix field of the main technical contact form
                if (this.MTCFormElements) VZDeliveryPlanner.validatePrefixField(this.MTCFormElements);
            }
            // Trigger change event on the checkout form to re-validate the form and update the submit button state
            this.querySelector('.js-checkout-form')?.dispatchEvent(new CustomEvent('change'));
        });
    }

    /**
     * When the installation contact forms are enabled, it will clear the form fields and sets them as required,
     * When the form is disabled, it pre-fills the form with either the main technical contact's data or the main contact's data,
     * depending on whether the main technical contact checkbox is checked.
     *
     * @param {HTMLElement} site - The installation site section element.
     * @param {Event} [event] - The change event from the checkbox (optional).
     */
    toggleInstallationSiteContact(site, event) {
        // Prevent the immediate change event from triggering form validation
        event?.stopPropagation();

        const ISCForm = site.querySelector('.js-installation-contact-form');
        const ISCCheckbox = site.querySelector('.js-contact-person-checkbox');
        const ISCFormElements = VZDeliveryPlanner.getContactFormElements(site);

        handleTransition(() => {
            VZDeliveryPlanner.syncExpandedState(ISCCheckbox);
            // Toggle the form display based on the checkbox status.
            ISCForm.classList.toggle('d-none', !ISCCheckbox.checked);

            if (!ISCCheckbox.checked) {
                // If main technical contact is enabled and checked, fill that one in, otherwise the main contact
                makeContactFormFieldsRequired(ISCFormElements, false);

                if (this.MTCEnabled && this.MTCCheckbox && this.MTCCheckbox.checked) {
                    prefillFormFields(ISCFormElements, this.MTCFormValues);
                } else {
                    prefillFormFields(ISCFormElements, this.mainContactData);
                }
            } else {
                // When the checkbox is checked, clear the form fields and set them as required.
                clearFormFields(ISCFormElements);
                makeContactFormFieldsRequired(ISCFormElements, true);

                // Validate the phone prefix field of the installation site contact form
                if (ISCFormElements) VZDeliveryPlanner.validatePrefixField(ISCFormElements);
            }
            // Trigger change event on the checkout form to re-validate the form and update the submit button state
            this.querySelector('.js-checkout-form')?.dispatchEvent(new CustomEvent('change'));
        });
    }
}

if (!window.customElements.get('vz-delivery-planner')) {
    window.customElements.define('vz-delivery-planner', VZDeliveryPlanner);
}
